import React, { useState, useRef } from 'react';
import { Mail, Phone, MapPin, Linkedin, Copy, Sparkles } from 'lucide-react';
import { Fireworks } from 'fireworks-js';
import confetti from 'canvas-confetti';
import meImage from '../assets/me.png';
import hiImage from '../assets/hi.png';
import memojiImage from '../assets/memoji.png'; 

export function Contact() {
    const [copied, setCopied] = useState(false);
    const [isWiggling, setIsWiggling] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const email = 'rrachelzhu@gmail.com';
    const phone = '+1 (437) 362-1756';
    const fireworksRef = useRef(null);
    
    const images = [meImage, hiImage, memojiImage];

    const handleMemojiClick = () => {
        // Cycle to next image
        setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
        
        setIsWiggling(true);
        setTimeout(() => setIsWiggling(false), 500);

        // Trigger confetti burst
        confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 }
        });

        // Trigger fireworks
        if (fireworksRef.current) {
            const fireworks = new Fireworks(fireworksRef.current, {
                autoresize: true,
                opacity: 0.5,
                acceleration: 1.05,
                friction: 0.97,
                gravity: 1.5,
                particles: 50,
                traceSpeed: 3,
                explosion: 5,
                intensity: 30,
                flickering: 50,
                lineStyle: 'round',
            });
            fireworks.start();
            setTimeout(() => {
                fireworks.stop();
                fireworks.clear();
            }, 3000);
        }
    };

    const triggerFireworks = () => {
        // Trigger a bigger fireworks show
        confetti({
            particleCount: 150,
            spread: 100,
            origin: { y: 0.6 },
            colors: ['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff']
        });

        if (fireworksRef.current) {
            const fireworks = new Fireworks(fireworksRef.current, {
                autoresize: true,
                opacity: 0.6,
                acceleration: 1.05,
                friction: 0.95,
                gravity: 1.3,
                particles: 80,
                traceSpeed: 4,
                explosion: 7,
                intensity: 40,
                flickering: 60,
                lineStyle: 'round',
            });
            fireworks.start();
            setTimeout(() => {
                fireworks.stop();
                fireworks.clear();
            }, 5000);
        }
    };

    const copyToClipboard = async (text, onSuccess) => {
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(text);
            } else {
                const el = document.createElement('textarea');
                el.value = text;
                el.setAttribute('readonly', '');
                el.style.position = 'absolute';
                el.style.left = '-9999px';
                document.body.appendChild(el);
                el.select();
                document.execCommand('copy');
                document.body.removeChild(el);
            }
            onSuccess(true);
            setTimeout(() => onSuccess(false), 2000);
        } catch (err) {
            // fallback: do nothing or optionally open tel/mail
            // For email we might fallback to mailto, but we won't auto-trigger phone dial
        }
    };

    const copyEmail = async () => {
        try {
            await copyToClipboard(email, setCopied);
        } catch (err) {
            // fallback: open mail client if copy fails
            window.location.href = `mailto:${email}`;
        }
    };

    // phone copy removed per UX request — keep phone as display only
    return (
        <section
            id="contact"
            className="relative flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 py-12"
        >
            <div
                ref={fireworksRef}
                className="absolute top-0 left-0 w-full h-full pointer-events-none"
                style={{ zIndex: 9999 }}
            />
            
            {/* Firework Icon Button - Desktop Only */}
            <button
                type="button"
                onClick={triggerFireworks}
                className="hidden lg:block fixed bottom-8 right-8 z-50 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-all transform hover:scale-110 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-purple-500"
                aria-label="Trigger fireworks"
            >
                <Sparkles className="h-6 w-6" />
            </button>

            {/*  Memoji */}
            <button
                type="button"
                aria-label="Rachel's profile image"
                onClick={handleMemojiClick}
                className="rounded-full p-1 mb-6 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-indigo-500 transition-transform transform hover:scale-105 active:scale-95 pulse-glow"
            >
                <img
                    src={images[currentImageIndex]}
                    alt="Rachel's Memoji"
                    className={`w-24 h-24 sm:w-32 sm:h-32 rounded-full ${isWiggling ? 'wiggle' : ''}`}
                />
            </button>

            <div className="text-center mb-8 px-4">
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 dark:text-white mb-4">
                    Get in Touch
                </h2>
                <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300">
                    Interested in working together? Let's connect!
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 max-w-4xl w-full">
                {/* Email Section */}
                <div 
                    className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-4 sm:p-6 hover:shadow-lg transition transform hover:scale-105 active:scale-95 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500"
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3 sm:space-x-4 min-w-0 flex-1">
                            <div className="bg-blue-100 dark:bg-blue-700 rounded-full p-2 flex-shrink-0">
                                <Mail className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600 dark:text-blue-300" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Email</p>
                                <p className="text-sm sm:text-lg text-gray-800 dark:text-gray-100 truncate">{email}</p>
                            </div>
                        </div>

                        <div className="flex items-center space-x-2 flex-shrink-0 ml-3 sm:ml-4">
                            <button
                                type="button"
                                onClick={copyEmail}
                                className="inline-flex items-center justify-center h-8 w-8 bg-white text-gray-600 dark:bg-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-indigo-500 transition"
                                aria-label="Copy email to clipboard"
                            >
                                <Copy className="h-4 w-4" />
                            </button>

                            {copied && (
                                <span className="text-xs sm:text-sm whitespace-nowrap text-green-500" aria-live="polite">
                                    Copied!
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Phone Section */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-4 sm:p-6 flex items-center justify-between space-x-3 sm:space-x-4 hover:shadow-lg transition transform hover:scale-105 active:scale-95 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500">
                    <div className="flex items-center space-x-3 sm:space-x-4">
                        <div className="bg-green-100 dark:bg-green-700 rounded-full p-2 flex-shrink-0">
                            <Phone className="h-5 w-5 sm:h-6 sm:w-6 text-green-600 dark:text-green-300" />
                        </div>
                        <div>
                            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Phone</p>
                            <p className="text-sm sm:text-lg text-gray-800 dark:text-gray-100">{phone}</p>
                        </div>
                    </div>

                    {/* Phone number is display-only (no automatic dial or copy) */}
                    <div />
                </div>

                {/* Location Section */}
                <a
                    href="https://www.google.com/maps?q=Toronto,+ON"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-4 sm:p-6 flex items-center space-x-3 sm:space-x-4 hover:shadow-lg transition transform hover:scale-105 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-indigo-500 cursor-pointer"
                    aria-label="Open location in Google Maps"
                >
                    <div className="bg-purple-100 dark:bg-purple-700 rounded-full p-2 flex-shrink-0">
                        <MapPin className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600 dark:text-purple-300" />
                    </div>
                    <div>
                        <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">Location</p>
                        <p className="text-sm sm:text-lg text-gray-800 dark:text-gray-100">Toronto, ON</p>
                    </div>
                </a>

                {/* LinkedIn Section */}
                <a
                    href="https://www.linkedin.com/in/rachelrzhu/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Open LinkedIn profile"
                    className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-4 sm:p-6 flex items-center space-x-3 sm:space-x-4 hover:shadow-lg transition transform hover:scale-105 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-indigo-500 cursor-pointer"
                >
                    <div className="bg-blue-100 dark:bg-blue-700 rounded-full p-2 flex-shrink-0">
                        <Linkedin className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600 dark:text-blue-300" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">LinkedIn</p>
                        <p className="text-sm sm:text-lg text-gray-800 dark:text-gray-100 truncate">
                            linkedin.com/in/rachelrzhu
                        </p>
                    </div>
                </a>
            </div>
        </section>
    );
}
